// src/pages/TokenCrud.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Typography, Paper, IconButton, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Checkbox, FormControlLabel, Tooltip,
  Autocomplete, CircularProgress
} from '@mui/material';
import {
  Add, Edit, Delete, Check, RadioButtonUnchecked, Clear,
  Lock, LockOpen
} from '@mui/icons-material';
import { debounce } from '@mui/material/utils';
import api from '../services/api';

function fmtMoneyBR(n) {
  if (n === null || n === undefined || n === '') return '—';
  const num = Number(n);
  if (Number.isNaN(num)) return '—';
  return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

const MAX_PRECO = 100.00;
const msgMaxPreco = () =>
  `Preço máximo permitido é R$ ${MAX_PRECO.toFixed(2).replace('.', ',')}.`;

export default function TokenCrud() {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);

  // filtro (admin): usuário selecionado
  const isAdmin = (localStorage.getItem('tipo') === 'admin');
  const [usuarioSel, setUsuarioSel] = useState(null);        // { nome, chave } | null
  const [userOptions, setUserOptions] = useState([]);
  const [userLoading, setUserLoading] = useState(false);

  // dialog criar/editar
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    nome: '',
    tipo: 'Mercado Pago',
    token: '',
    kit_preco: '',
    ativo: 1,
    padrao: 0
  });

  const normalizePreco = (v) => {
    if (v === '' || v === null || v === undefined) return null;
    const num = Number(String(v).replace(',', '.'));
    return Number.isNaN(num) ? null : num;
  };

  // ===== BUSCA tokens (respeitando admin x não-admin) =====
  const fetchTokens = async () => {
    setLoading(true);
    try {
      const params = {};
      if (isAdmin) {
        if (usuarioSel?.chave) params.chave_usuario = usuarioSel.chave;
      } else {
        params.chave_usuario = localStorage.getItem('chave_usuario');
      }

      const res = await api.get('/tokens', { params: Object.keys(params).length ? params : undefined });
      setTokens(res.data.registros || []);
    } catch (err) {
      console.error(err);
      alert('Erro ao buscar tokens');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTokens(); }, []);
  useEffect(() => { if (isAdmin) fetchTokens(); }, [isAdmin, usuarioSel?.chave]);

  // ===== Busca digitável de usuários (admin) =====
  const doSearchUsers = useMemo(() => debounce(async (q) => {
    if (!q) { setUserOptions([]); return; }
    try {
      setUserLoading(true);
      const { data } = await api.get('/usuarios-busca', { params: { q } });
      setUserOptions(data || []);
    } catch (e) {
      console.error('Erro busca usuários:', e);
    } finally {
      setUserLoading(false);
    }
  }, 300), []);

  // ===== CRUD =====
  const handleOpen = (token = null) => {
    setEditing(token);
    setForm(token
      ? { ...token, kit_preco: token.kit_preco ?? '' }
      : { nome: '', tipo: 'Mercado Pago', token: '', kit_preco: '', ativo: 1, padrao: 0 }
    );
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
    setForm({ nome: '', tipo: 'Mercado Pago', token: '', kit_preco: '', ativo: 1, padrao: 0 });
  };

  const handleSave = async () => {
    if (!form.nome || !form.token) {
      alert('Preencha nome e token.');
      return;
    }

    const precoNormalizado = normalizePreco(form.kit_preco);
    if (precoNormalizado !== null && precoNormalizado > MAX_PRECO) {
      alert(msgMaxPreco());
      return;
    }

    setLoading(true);
    const payloadBase = {
      ...form,
      kit_preco: normalizePreco(form.kit_preco),
      ativo: form.ativo ? 1 : 0,
      padrao: form.padrao ? 1 : 0
    };

    try {
      if (editing) {
        await api.put(`/tokens/${editing.id}`, {
          ...payloadBase,
          chave_usuario: editing.chave_usuario || localStorage.getItem('chave_usuario'),
        });
      } else {
        const chave_usuario =
          (isAdmin && usuarioSel?.chave) ? usuarioSel.chave : localStorage.getItem('chave_usuario');

        await api.post('/tokens', { ...payloadBase, chave_usuario });
      }
      handleClose();
      fetchTokens();
    } catch (e) {
      console.error(e);
      alert('Erro ao salvar token');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (token) => {
    if (!window.confirm('Deseja remover este token?')) return;
    setLoading(true);
    try {
      await api.delete(`/tokens/${token.id}`);
      fetchTokens();
    } catch (e) {
      console.error(e);
      alert('Erro ao remover token');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPadrao = async (token) => {
    setLoading(true);
    try {
      await api.put(`/tokens/${token.id}`, {
        ...token,
        padrao: 1,
        chave_usuario: token.chave_usuario,
      });
      fetchTokens();
    } catch (e) {
      console.error(e);
      alert('Erro ao definir como padrão');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAtivo = async (token) => {
    setLoading(true);
    try {
      await api.put(`/tokens/${token.id}`, {
        ...token,
        ativo: token.ativo ? 0 : 1,
        chave_usuario: token.chave_usuario,
      });
      fetchTokens();
    } catch (e) {
      console.error(e);
      alert('Erro ao atualizar status');
    } finally {
      setLoading(false);
    }
  };

  const tipoOptions = [{ value: 'Mercado Pago', label: 'Mercado Pago' }];

  return (
    <Box p={3}>
      <Typography variant="h5" gutterBottom>
        Tokens de Integração (Mercado Pago)
      </Typography>

      {/* Filtro admin: buscar revendedor/licença */}
      {isAdmin && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Autocomplete
            size="small"
            sx={{ width: 320 }}
            options={userOptions}
            loading={userLoading}
            filterOptions={(x) => x}
            value={usuarioSel}
            onChange={(_e, val) => setUsuarioSel(val)}
            onInputChange={(_e, q) => doSearchUsers(q)}
            getOptionLabel={(o) => (o ? `${o.nome} — ${o.chave}` : '')}
            clearOnEscape
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buscar revendedor/licença (admin)"
                placeholder="Digite nome ou chave..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {userLoading ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Button
            size="small"
            variant="outlined"
            startIcon={<Clear />}
            onClick={() => { setUsuarioSel(null); fetchTokens(); }}
          >
            Limpar
          </Button>
        </Box>
      )}

      <Button
        variant="contained"
        startIcon={<Add />}
        sx={{ mb: 2 }}
        onClick={() => handleOpen(null)}
      >
        Novo Token
      </Button>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Tipo</TableCell>
              <TableCell>Nome</TableCell>
              <TableCell>Usuário</TableCell>
              <TableCell>Token (chave)</TableCell>
              <TableCell align="right">Kit-Blindagem</TableCell>
              <TableCell align="center">Padrão</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="right" width={200}>Ações</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {tokens.map((t) => (
              <TableRow key={t.id} hover>
                <TableCell>{t.tipo}</TableCell>
                <TableCell>{t.nome}</TableCell>
                <TableCell>{t.usuario_nome || '-'}</TableCell>

                <TableCell
                  style={{
                    fontFamily: 'monospace',
                    maxWidth: 320,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  width={320}
                >
                  {t.token}
                </TableCell>

                <TableCell align="right" width={140}>{fmtMoneyBR(t.kit_preco)}</TableCell>

                <TableCell align="center">
                  {t.padrao
                    ? <Check color="success" fontSize="medium" />
                    : (
                      <Tooltip title="Tornar padrão">
                        <IconButton onClick={() => handleSetPadrao(t)}>
                          <RadioButtonUnchecked color="action" />
                        </IconButton>
                      </Tooltip>
                    )
                  }
                </TableCell>

                {/* Status APENAS indicador (sem click) */}
                <TableCell align="center">
                  <span
                    title={t.ativo ? 'Ativo' : 'Inativo'}
                    style={{ display: 'inline-block', fontSize: 18, lineHeight: 1 }}
                    aria-label={t.ativo ? 'Ativo' : 'Inativo'}
                  >
                    {t.ativo ? '🟢' : '🔴'}
                  </span>
                </TableCell>

                {/* Ações com cadeado para ativar/desativar */}
                <TableCell align="right" width={200}>
                  <Tooltip title={t.ativo ? 'Desativar (bloquear)' : 'Ativar (desbloquear)'}>
                    <IconButton onClick={() => handleToggleAtivo(t)}>
                      {t.ativo
                        ? <LockOpen sx={{ color: 'warning.main' }} />
                        : <Lock sx={{ color: 'warning.main' }} />
                      }
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Editar">
                    <IconButton color="primary" onClick={() => handleOpen(t)}>
                      <Edit />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Excluir">
                    <IconButton color="error" onClick={() => handleDelete(t)}>
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}

            {!loading && tokens.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Nenhum token cadastrado.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog criar/editar */}
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>{editing ? 'Editar Token' : 'Novo Token'}</DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <TextField
            label="Nome"
            value={form.nome}
            onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}
            fullWidth
            sx={{ mb: 2 }}
            autoFocus
          />
          <TextField
            select
            label="Tipo"
            value={form.tipo}
            onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}
            fullWidth
            sx={{ mb: 2 }}
            SelectProps={{ native: true }}
          >
            {tipoOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </TextField>
          <TextField
            label="Token (chave de acesso)"
            value={form.token}
            onChange={e => setForm(f => ({ ...f, token: e.target.value }))}
            fullWidth
            multiline
            minRows={2}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Preço Kit-Blindagem (R$)"
            type="number"
            inputProps={{ step: '0.01', min: '0', max: String(MAX_PRECO) }}
            value={form.kit_preco}
            onChange={e => setForm(f => ({ ...f, kit_preco: e.target.value }))}

            onBlur={() => {
              const v = normalizePreco(form.kit_preco);
              if (v !== null && v > MAX_PRECO) {
                alert(msgMaxPreco());
                setForm(f => ({ ...f, kit_preco: String(MAX_PRECO) }));
              }
            }}

            fullWidth
            sx={{ mb: 2 }}
            placeholder="Ex: 49.90"
            helperText={`Opcional. Máx: R$ ${MAX_PRECO.toFixed(2).replace('.', ',')}`}
          />
          <FormControlLabel
            control={<Checkbox checked={!!form.padrao} onChange={e => setForm(f => ({ ...f, padrao: e.target.checked ? 1 : 0 }))} />}
            label="Token padrão para cobranças"
          />
          <FormControlLabel
            control={<Checkbox checked={!!form.ativo} onChange={e => setForm(f => ({ ...f, ativo: e.target.checked ? 1 : 0 }))} />}
            label="Ativo"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">Cancelar</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
